import { formatShortDateTime } from '@/util/date-util';

/** Above this, the evaluator itself is behind and every number on the page is suspect. */
export const EVALUATION_STALE_MS = 20 * 60 * 1000;

/**
 * "il y a 3 h", "3h ago". Relative rather than absolute because every question this page answers is
 * about elapsed time — how long has it been broken, when did it last work — and a wall-clock stamp
 * makes the reader do the subtraction.
 */
export function formatRelativeToNow(value, locale) {
  if (!value) return '—';

  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '—';

  const minutes = Math.round((Date.now() - then) / 60000);
  if (minutes < 0) return formatDuration(0, locale);
  return formatDuration(minutes, locale);
}

/**
 * The absolute timestamp, for a tooltip behind a relative one.
 *
 * Relative time answers "should I worry" at a glance, which is what the page is for, but it loses the
 * information you need the moment you want to correlate with anything else — a deploy, a log line, a
 * source's publishing schedule. "181 d 23 h ago" is unusable for that; the date is not.
 */
export function formatAbsolute(value) {
  // Delegates to date-util rather than formatting here: it is the app-wide convention
  // (YYYY-MM-DD HH:mm), moment's locale already follows the UI language via localize(), and its
  // toMoment() copes with more input shapes than `new Date` does — including the {epochSecond, nano}
  // object Jackson emits without a JavaTimeModule, which is exactly what broke every timestamp on
  // this page once already.
  return value ? formatShortDateTime(value) : '';
}

/**
 * Minutes as something readable at a glance — relative, and localised:
 * "just now", "45 min ago", "3 h ago", "3 h 20 ago", "4 d ago", "4 d 5 h ago",
 * and in French "à l'instant", "il y a 45 min", "il y a 3 h 20", "il y a 4 j".
 *
 * The hour+minute case carries no unit on the minutes ("3 h 20") — the French convention, applied to
 * both locales. `scraper-health-util.spec.js` pins that, so it is a decision rather than a slip.
 */
export function formatDuration(minutes, locale) {
  const isFr = String(locale || '').startsWith('fr');
  const ago = (text) => (isFr ? `il y a ${text}` : `${text} ago`);

  if (minutes < 1) return isFr ? "à l'instant" : 'just now';
  if (minutes < 60) return ago(`${minutes} min`);

  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  if (hours < 24) return ago(remMinutes === 0 ? `${hours} h` : `${hours} h ${remMinutes}`);

  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  const dayLabel = isFr ? 'j' : 'd';
  return ago(remHours === 0 ? `${days} ${dayLabel}` : `${days} ${dayLabel} ${remHours} h`);
}

/**
 * Whether a lane should draw attention. `paused` deliberately does not: it is a decision someone
 * recorded, not a fault.
 */
export function isLaneProblem(lane) {
  return Boolean(lane) && lane.state !== 'ok' && lane.state !== 'paused';
}

/**
 * The one answer a non-engineer wants: is this source delivering tenders or not.
 *
 * The API's own `overallState` is the max severity across lanes, in the evaluator's vocabulary. That
 * is right for triage and wrong for this question, because it flattens two very different situations
 * into "failing": bidsandtenders (crawling fine, detail fetches failing — tenders ARE arriving, just
 * thin) and saskatchewan (crawl dead — nothing arriving at all) both come back `failing`.
 *
 * So this rolls the lanes up by consequence instead of by severity:
 *
 * - `down`      nothing new is reaching clients. Either the crawl is broken, so no tenders are being
 *                found at all, or indexing is broken, so whatever is found never reaches search —
 *                and a tender a client cannot find does not exist as far as the app is concerned.
 * - `degraded`  tenders are arriving and are findable, but something is wrong behind them: detail
 *                pages are not being fetched, or search is lagging. Real, not urgent.
 * - `unknown`   we could not measure the lanes that decide the above.
 * - `paused`    someone owns it and recorded why.
 * - `ok`        everything working.
 *
 * NOTE: this is presentation-side today. If the notifications ever want to speak in these terms too,
 * it belongs in aloha.api.tender beside `overallState` so both sides share one definition — the rule
 * itself is a product decision, not a rendering detail.
 */
export function getSourceServiceLevel(source) {
  if (!source) return 'unknown';
  if (source.paused) return 'paused';

  const lane = (kind) => (source.lanes || []).find((l) => l.kind === kind) || source[kind];
  const stateOf = (kind) => {
    const l = lane(kind);
    return l ? l.state : null;
  };

  const crawl = stateOf('crawl');
  const index = stateOf('index');
  const scrape = stateOf('scrape');

  // Nothing new reaching clients.
  if (crawl === 'failing' || crawl === 'stale') return 'down';
  if (index === 'failing') return 'down';

  // Arriving, but not whole.
  if (isProblemState(scrape) || isProblemState(index)) return 'degraded';

  // Cannot see the lanes that decide it. Never reported as working.
  if (crawl === 'unknown' || crawl === null) return 'unknown';

  return 'ok';
}

function isProblemState(state) {
  return state === 'failing' || state === 'stale' || state === 'unknown';
}
